/**
 * The board's first load in one request (`GET {root}/bootstrap`), and how its
 * answer lands in the per-slice caches the board already reads.
 *
 * Bootstrap is only a faster way to fill those caches: every entry is written
 * under the slice query's own key, so refetching, invalidation after writes,
 * "load more" and filter changes keep going through the slice queries exactly
 * as before.
 *
 * Pure, so the request and the cache entries can be tested.
 */
import { portalKeys } from "../api/supplierPortalQueryKeys.ts";
import type { ColumnPage, PortalBoardBootstrap, PortalBoardFilters, PortalScope } from "../types.ts";
import { serializeFilters } from "./boardFilters.ts";

/** The query string: the column pages' own filter keys, the window always, and the first pages. */
export function bootstrapParams(filters: PortalBoardFilters, limit: number): Record<string, string | number> {
  return {
    ...Object.fromEntries(serializeFilters(filters).entries()),
    window: filters.window,
    pages: 1,
    limit,
  };
}

export interface BootstrapCacheEntry {
  queryKey: readonly unknown[];
  data: unknown;
}

/** An infinite query holding only its first page; the next cursor still loads more. */
function firstPageOnly(page: ColumnPage) {
  return { pages: [page], pageParams: [null as string | null] };
}

/**
 * The cache entries a bootstrap answer stands for. `filters` and `pageSize`
 * must be the ones the request was made with, so each column lands under the
 * key `useColumnOrders` will ask for.
 */
export function bootstrapCacheEntries(
  scope: PortalScope,
  filters: PortalBoardFilters,
  pageSize: number,
  data: PortalBoardBootstrap,
): BootstrapCacheEntry[] {
  const { window } = filters;
  return [
    { queryKey: portalKeys.board(scope), data: data.board },
    { queryKey: portalKeys.summary(scope, window), data: data.summary },
    { queryKey: portalKeys.methods(scope, window), data: data.methods },
    { queryKey: portalKeys.counts(scope, window), data: data.counts },
    ...Object.entries(data.pages ?? {}).map(([columnId, page]) => ({
      queryKey: portalKeys.column(scope, columnId, filters, pageSize),
      data: firstPageOnly(page),
    })),
  ];
}

/**
 * Bootstrap only a cold board: never visited, or its window's reads already
 * collected. A warm board (back from an order page) shows its cache at once
 * and lets each slice refresh itself, as it always has. The board alone being
 * cached (arrived from an order page) still counts as cold.
 */
export function needsBootstrap(cache: { boardCached: boolean; countsCached: boolean }): boolean {
  return !cache.boardCached || !cache.countsCached;
}
